export const reservationInvoicePanel = {
  loadingVerifying: "Verifying…",

  status: {
    draft: "Draft",
    sent: "Issued",
    processing: "Processing",
    pending: "Pending",
    error: "Error",
    corrected: "Corrected",
    voided: "Voided",
  },

  common: {
    cancel: "Cancel",
    understood: "Got it",
    optional: "optional",
    optionalParen: "(optional)",
    sending: "Sending…",
    saving: "Saving…",
    deleting: "Deleting…",
    emitting: "Issuing…",
    creating: "Creating…",
    retrying: "Retrying…",
  },

  issued: {
    title: "Issued invoices",
    rectifOf: "Corrective of {code}",
    viewPdf: "View PDF",
    sendByWhatsapp: "Send via WhatsApp",
    correctTitle: "Correct (corrective)",
    voidTitle: "Void (corrective invoice)",
  },

  empty: {
    guestRequested: "The guest requested their invoice",
    noInvoice: "No invoice issued",
    linkSent: "Link sent",
    requested: "Requested",
    requestedSub: "Review their details and issue the invoice, or send them the link to confirm.",
    sub: "Send the link to the guest or create the invoice directly.",
    copyLinkTitle: "Copy link to clipboard",
    sendLink: "Send link",
    createInvoice: "Create invoice",
    configureHint: "Set up your billing details to enable this feature.",
  },

  next: {
    title: "This booking already has its invoice",
    sub: "Was there a later charge? Create another invoice for this booking: it will only include the new charges (the accommodation already invoiced isn't repeated).",
    button: "Create another invoice for this booking",
  },

  card: {
    draftRefTitle: "Internal draft reference. The fiscal number is assigned on issue.",
  },

  draft: {
    emptyHint: 'Add at least one charge under <b>“New charges”</b> to issue this invoice.',
    noteLead: "Draft — edit the details or add charges above; the number and date are assigned on <b>issue</b>.",
    nextSequential: "Next sequential number",
    estimated: "estimated",
    noteFoot: "The number isn't final: the first to issue takes it.",
    confirmEmit: "Confirm and issue invoice",
    previewPdf: "Preview PDF",
    sendQuote: "Send quote",
    edit: "Edit invoice",
    discard: "Discard",
  },

  // Concepts (line items) of the invoice in progress: what gets reviewed BEFORE issuing.
  concepts: {
    title: "Document line items",
    item: "Item",
    qty: "Qty",
    unitPrice: "Unit price",
    vat: "VAT",
    amount: "Amount",
    linesTotal: "Line items total",
    foot: "This is how the breakdown will appear on the invoice. The PDF has the full detail.",
    mismatch: "The line items total ({lines}) does not match the document total ({total}). Check it before issuing.",
  },

  // Lo cobrado y lo que queda a deber. Mismas palabras y mismo orden que el
  // pie del PDF, para que leer la pantalla y leer la factura sea lo mismo.
  cobro: {
    total: "Document total",
    nothing: "No payments recorded",
    due: "Outstanding",
    assumed: "No payments are recorded on this booking. The amount is taken as collected from the payment method set, and the PDF will say the same. If you haven't been paid yet, record the payment or change the payment method.",
  },

  actions: {
    sendWhatsapp: "Send WhatsApp",
    correct: "Correct",
    void: "Void",
    refresh: "Refresh",
    retry: "Retry",
    editData: "Edit details",
    delete: "Delete",
  },

  wa: {
    title: "Send invoice via WhatsApp",
    desc: "Invoice <strong>{code}</strong> will be sent to the guest as a PDF via WhatsApp.",
    issuedFallback: "issued",
    checkPhone: "The guest's phone linked to the booking will be used",
    checkPdf: "The document will be sent as a PDF",
    send: "Send invoice",
  },

  sendLink: {
    title: "Send billing link",
    desc: "The guest will be sent a <strong>WhatsApp link</strong> to confirm their tax details and download their invoice.",
    checkNoInvoice: "No invoice is issued yet: the guest only receives the link",
    button: "Send link",
  },

  retryModal: {
    title: "Retry issuance",
    desc: "We'll try to <strong>issue this invoice</strong> again with the current details.",
    check1: "It's resent to the e-invoicing authority",
    check2: "If it succeeds, the final fiscal number is assigned",
  },

  voidModal: {
    doneTitle: "Invoice voided — Corrective invoice issued",
    creditNoteIssued: "Corrective invoice <strong>{code}</strong> issued",
    chargesReleased: "The additional charges have been released",
    clientCanRequest: "The customer can request the invoice again",
    doneHint: "Add or correct the pending charges and create a new invoice.",
    formTitle: "Void invoice / Issue corrective invoice",
    formDesc: "Use this option when you need to <strong>add or change services/amounts</strong>. A <strong>corrective invoice</strong> will be issued that voids the original invoice, and you'll be able to create a new invoice with the correct charges.",
    stepAnnul: "A fiscal corrective invoice is issued that voids {code}",
    stepFree: "The charges are freed up to add or correct",
    stepNewNumber: "A new invoice is created with the next number",
    notice: "To correct only the NIF, name or address without changing amounts, use <strong>Correct details</strong> instead.",
    voiding: "Issuing corrective invoice…",
    confirm: "Void and issue corrective invoice",
  },

  edit: {
    titleDraft: "Edit invoice",
    titleClient: "Edit customer details",
    descDraft: "Changes are saved instantly. Tax details are validated without issuing.",
    descSent: "On save, sending will be retried automatically.",
    type: "Type",
    individual: "Individual",
    individualSub: "Natural person",
    company: "Company",
    companySub: "Legal entity",
    legalName: "Legal name",
    fullName: "Full name",
    legalNamePlaceholder: "Company legal name",
    fullNamePlaceholder: "First and last name",
    country: "Country",
    countryPlaceholder: "Select the guest's country",
    taxRequiredOnEmit: "required on issue",
    taxIdPlaceholderCompany: "Company NIF / CIF",
    taxIdPlaceholderIndividual: "DNI / NIE · empty = final consumer",
    email: "Email",
    emailPlaceholder: "email@example.com",
    phone: "Phone",
    phonePlaceholder: "+34 600 000 000",
    fiscalAddress: "Tax address",
    addressPlaceholder: "Street and number",
    city: "City",
    region: "Region / Province",
    zip: "Postal code",
    saveChanges: "Save changes",
    saveAndRetry: "Save and retry",
  },

  deleteModal: {
    title: "Delete invoice",
    desc: "This invoice attempt will be deleted. The customer will be able to request the invoice again from scratch.",
    noticeIrreversible: "This action can't be undone.",
  },

  emit: {
    title: "Confirm and issue invoice",
    subtitle: "It's officially recorded",
    desc: "You're about to issue the invoice for {name}.",
    stepNumberEstimate: "It's assigned the sequential number <b>{code}</b> — the first to issue takes it.",
    stepNumberFinal: "It's assigned the <b>final</b> sequential number — the first to issue takes it.",
    stepPdf: "The fiscal PDF is generated and filed with the tax authority.",
    warn: "Once issued it <b>can't be edited</b>. If there's an error, you'll have to <b>void it and create a new invoice</b> (corrective invoice).",
    confirm: "Confirm and issue",
  },

  newInvoice: {
    title: "Create another invoice",
    subtitle: "For this same booking",
    desc: "A <strong>new draft</strong> will open for this booking.",
    step1: "It will include <b>only the new charges</b> you add — the accommodation already invoiced isn't repeated.",
    step2: "It takes the same customer's details. The number is assigned on issue.",
    createDraft: "Create draft",

    // Facturar toda la reserva o una sola habitación (reservas de varias).
    scopeTitle: "How do you want to invoice?",
    whole: "The whole booking",
    wholeHint: "A single invoice with all {n} rooms.",
    perRoom: "One room",
    perRoomHint: "Each room, its own invoice. For when each guest pays their part.",
    pickRoom: "Pick the room",
    roomLodging: "Accommodation",
    alreadyInvoiced: "already invoiced",
  },

  correction: {
    doneTitle: "Corrective issued",
    createdOk: "Corrective <strong>{code}</strong> created successfully",
    originalKept: "The original invoice remains as a fiscal reference (it isn't deleted)",
    pdfSoon: "The PDF will be generated shortly",
    title: "Correct invoice",
    infobox: "A <strong>corrective corrective invoice</strong> will be issued that fiscally replaces <strong>{code}</strong> and includes the booking's current charges. The original number can't be changed (it's the law).",
    reasonLabel: "Reason for the correction",
    selectReason: "— Select a reason —",
    reasonTaxId: "Incorrect NIF / CIF or Tax ID",
    reasonName: "Incorrect name or legal name",
    reasonAddress: "Incorrect tax address",
    reasonEmail: "Incorrect email",
    reasonGeneral: "General correction of tax details",
    reasonOther: "Other (write reason)",
    customReasonPlaceholder: "Describe the reason for the correction…",
    recipientDivider: "Recipient details — change only what you need to correct",
    nameOrLegalName: "Name / Legal name",
    taxIdFull: "NIF / CIF / Tax ID / Passport",
    countryTwoLetters: "(2 letters)",
    paymentDivider: "Payment method",
    paymentLabel: "Payment type",
    paymentUnspecified: "— Unspecified —",
    paymentCash: "Cash",
    paymentCard: "Credit / debit card",
    paymentTransfer: "Bank transfer",
    paymentStripe: "Stripe (online)",
    emitRectif: "Issue corrective",
  },

  request: {
    typeCompany: "Company",
    typeIndividual: "Individual",
    type: "Type",
    legalName: "Legal name / Name",
    taxId: "NIF / CIF",
    address: "Address",
    postalCode: "Postcode",
    city: "City",
    state: "Province",
    country: "Country",
    email: "Email",
  },

  toast: {
    linkSent: "Billing link sent to the guest via WhatsApp.",
    alreadyIssued: "This booking already has an issued invoice.",
    cantSendLink: "The link couldn't be sent.",
    linkCopied: "Link copied to clipboard.",
    alreadyHasInvoice: "This booking already has an invoice.",
    cantGenerateLink: "The link couldn't be generated.",
    cantSendWhatsapp: "It couldn't be sent via WhatsApp.",
    invoiceSentWhatsapp: "Invoice sent via WhatsApp.",
    draftCreatedNewCharges: "Draft created with the new charges (without repeating the accommodation). Review it and issue it.",
    draftCreated: "Draft created. Add charges and issue it when it's ready.",
    alreadyInProgress: "This booking already has an invoice in progress.",
    cantCreateDraft: "The draft couldn't be created.",
    invoiceEmitted: "Invoice issued. Generating the number and the PDF…",
    cantEmit: "The invoice couldn't be issued.",
    cantPreview: "The preview couldn't be generated.",
    quoteSent: "Quote sent to {email}",
    cantSendQuote: "Couldn't send the quote",
    cantSync: "The status couldn't be synced.",
    retrying: "Retrying issuance…",
    cantRetry: "Couldn't retry.",
    cantVoid: "Error voiding the invoice.",
    savedCheckFiscal: "Saved. Check the fiscal document.",
    dataUpdated: "Details updated.",
    cantSaveData: "The details couldn't be saved.",
    invoiceDeleted: "Invoice deleted.",
    cantDelete: "Couldn't delete.",
    cantEmitRectif: "Error issuing the corrective.",
  },
};
